import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fecha } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Etiqueta, Modal, Panel, Vacio } from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Descuento {
  id: string;
  nombre: string;
  porcentaje: number;
  categoriaId: string | null;
  productoId: string | null;
  tipoSocioId: string | null;
  diasSemana: number[];
  horaInicio: string | null;
  horaFin: string | null;
  fechaInicio: string | null;
  fechaFin: string | null;
  activo: boolean;
  categoria: { nombre: string } | null;
  producto: { nombre: string } | null;
  tipoSocio: { nombre: string } | null;
}

interface Opcion {
  id: string;
  nombre: string;
}

/** De lunes a domingo, en el orden en que se leen. */
const DIAS = [1, 2, 3, 4, 5, 6, 0];

/** Descuentos de campana: se aplica el mayor que encaje; no se acumulan. */
export default function Descuentos() {
  const { t } = useIdioma();
  const { puede } = useSesion();

  const nombreDia = (v: number) => t(`semana.${v}c` as Clave);
  const nombreDiaLargo = (v: number) => t(`semana.${v}` as Clave);
  const clienteConsultas = useQueryClient();
  const [editando, setEditando] = useState<Descuento | "nuevo" | null>(null);
  const [error, setError] = useState("");

  const descuentos = useQuery({ queryKey: ["descuentos"], queryFn: () => api<Descuento[]>("/descuentos") });
  const categorias = useQuery({ queryKey: ["categorias"], queryFn: () => api<Opcion[]>("/catalogo/categorias") });
  const productos = useQuery({ queryKey: ["productos"], queryFn: () => api<Opcion[]>("/catalogo/productos") });
  const tipos = useQuery({ queryKey: ["tipos-socio"], queryFn: () => api<Opcion[]>("/tipos-socio") });

  const refrescar = () => void clienteConsultas.invalidateQueries({ queryKey: ["descuentos"] });

  async function borrar(d: Descuento) {
    if (!window.confirm(`¿Borrar el descuento "${d.nombre}"?`)) return;
    try {
      await api(`/descuentos/${d.id}`, { metodo: "DELETE" });
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("de.falloBorrar"));
    }
  }

  return (
    <>
      <Cabecera titulo={t("de.titulo")} descripcion={t("de.descripcion")}>
        {puede("gestionarCatalogo") && (
          <button className="boton" onClick={() => setEditando("nuevo")}>
            {t("de.nuevo")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="info">
        {t("de.enCadaEntrega")} <strong>{t("de.unoSolo")}</strong> {t("de.noSeAcumulan")}
      </Aviso>

      <Panel sinCuerpo>
        {descuentos.isLoading ? (
          <Cargando />
        ) : !descuentos.data || descuentos.data.length === 0 ? (
          <Vacio>{t("de.sinDescuentos")}</Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th>{t("de.nombre")}</th>
                  <th className="num">{t("de.descuento")}</th>
                  <th>{t("de.aplicaA")}</th>
                  <th>{t("de.cuando")}</th>
                  <th>{t("de.estado")}</th>
                  {puede("gestionarCatalogo") && <th />}
                </tr>
              </thead>
              <tbody>
                {descuentos.data.map((d) => (
                  <tr key={d.id} style={d.activo ? undefined : { opacity: 0.55 }}>
                    <td>
                      <strong>{d.nombre}</strong>
                    </td>
                    <td className="num tabular">−{d.porcentaje}%</td>
                    <td className="pequeno">
                      {d.producto
                        ? d.producto.nombre
                        : d.categoria
                          ? t("de.categoriaNombre", { nombre: d.categoria.nombre })
                          : t("de.todoElCatalogo")}
                      {d.tipoSocio && (
                        <div className="suave">{t("de.soloTipo", { tipo: d.tipoSocio.nombre })}</div>
                      )}
                    </td>
                    <td className="pequeno">
                      {d.diasSemana.length === 0
                        ? t("de.todosLosDias")
                        : // Con uno o dos días se escribe entero: una "M" suelta
                          // en una tabla no dice si es martes o miércoles.
                          d.diasSemana.length <= 2
                          ? d.diasSemana.map(nombreDiaLargo).join(" y ")
                          : d.diasSemana.map(nombreDia).join(" ")}
                      {d.horaInicio && d.horaFin && (
                        <div className="suave">
                          {d.horaInicio}–{d.horaFin}
                        </div>
                      )}
                      {(d.fechaInicio || d.fechaFin) && (
                        <div className="suave">
                          {d.fechaInicio ? fecha(d.fechaInicio) : "…"} → {d.fechaFin ? fecha(d.fechaFin) : "…"}
                        </div>
                      )}
                    </td>
                    <td>
                      {d.activo ? (
                        <Etiqueta tipo="verde">{t("de.activo")}</Etiqueta>
                      ) : (
                        <Etiqueta>{t("de.apagado")}</Etiqueta>
                      )}
                    </td>
                    {puede("gestionarCatalogo") && (
                      <td>
                        <div className="fila" style={{ gap: 2, justifyContent: "flex-end" }}>
                          <button className="boton fantasma" onClick={() => setEditando(d)}>
                            {t("de.editar")}
                          </button>
                          <button className="boton fantasma" onClick={() => void borrar(d)}>
                            {t("de.borrar")}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {editando && (
        <DialogoDescuento
          descuento={editando === "nuevo" ? null : editando}
          categorias={categorias.data ?? []}
          productos={productos.data ?? []}
          tipos={tipos.data ?? []}
          alCerrar={() => setEditando(null)}
          alFallar={setError}
          alHecho={() => {
            setEditando(null);
            refrescar();
          }}
        />
      )}
    </>
  );
}

function DialogoDescuento({
  descuento,
  categorias,
  productos,
  tipos,
  alCerrar,
  alHecho,
  alFallar,
}: {
  descuento: Descuento | null;
  categorias: Opcion[];
  productos: Opcion[];
  tipos: Opcion[];
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [datos, setDatos] = useState({
    nombre: descuento?.nombre ?? "",
    porcentaje: String(descuento?.porcentaje ?? 10),
    categoriaId: descuento?.categoriaId ?? "",
    productoId: descuento?.productoId ?? "",
    tipoSocioId: descuento?.tipoSocioId ?? "",
    diasSemana: descuento?.diasSemana ?? ([] as number[]),
    horaInicio: descuento?.horaInicio ?? "",
    horaFin: descuento?.horaFin ?? "",
    fechaInicio: descuento?.fechaInicio?.slice(0, 10) ?? "",
    fechaFin: descuento?.fechaFin?.slice(0, 10) ?? "",
    activo: descuento?.activo ?? true,
  });
  const [enviando, setEnviando] = useState(false);

  const cambiar = (campo: string, valor: unknown) => setDatos((d) => ({ ...d, [campo]: valor }));
  const alternarDia = (v: number) =>
    cambiar("diasSemana", datos.diasSemana.includes(v) ? datos.diasSemana.filter((d) => d !== v) : [...datos.diasSemana, v]);

  async function guardar() {
    setEnviando(true);
    try {
      const cuerpo = {
        nombre: datos.nombre,
        porcentaje: Number(datos.porcentaje),
        categoriaId: datos.categoriaId || null,
        productoId: datos.productoId || null,
        tipoSocioId: datos.tipoSocioId || null,
        diasSemana: datos.diasSemana,
        horaInicio: datos.horaInicio || null,
        horaFin: datos.horaFin || null,
        fechaInicio: datos.fechaInicio || null,
        fechaFin: datos.fechaFin || null,
        activo: datos.activo,
      };
      if (descuento) await api(`/descuentos/${descuento.id}`, { metodo: "PATCH", cuerpo });
      else await api("/descuentos", { metodo: "POST", cuerpo });
      alHecho();
    } catch (fallo) {
      alFallar(fallo instanceof Error ? fallo.message : t("de.d.fallo"));
      alCerrar();
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      titulo={descuento ? t("de.d.editar", { nombre: descuento.nombre }) : t("de.d.nuevo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button className="boton" onClick={guardar} disabled={enviando || !datos.nombre || !(Number(datos.porcentaje) >= 1)}>
            {enviando ? t("g.guardando") : t("g.guardar")}
          </button>
        </>
      }
    >
      <div className="linea-campos">
        <Campo etiqueta={t("de.d.nombre")} ayuda={t("de.d.nombreAyuda")}>
          <input value={datos.nombre} onChange={(e) => cambiar("nombre", e.target.value)} autoFocus />
        </Campo>
        <Campo etiqueta={t("de.d.porcentaje")}>
          <input type="number" min={1} max={100} value={datos.porcentaje} onChange={(e) => cambiar("porcentaje", e.target.value)} />
        </Campo>
      </div>

      <div className="linea-campos">
        <Campo etiqueta={t("de.d.categoria")} ayuda={t("de.d.categoriaAyuda")}>
          <select value={datos.categoriaId} onChange={(e) => cambiar("categoriaId", e.target.value)}>
            <option value="">{t("de.d.todas")}</option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta={t("de.d.producto")} ayuda={t("de.d.productoAyuda")}>
          <select value={datos.productoId} onChange={(e) => cambiar("productoId", e.target.value)}>
            <option value="">{t("de.d.todos")}</option>
            {productos.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      <Campo etiqueta={t("de.d.tipoSocio")} ayuda={t("de.d.tipoSocioAyuda")}>
        <select value={datos.tipoSocioId} onChange={(e) => cambiar("tipoSocioId", e.target.value)}>
          <option value="">{t("de.d.todosLosSocios")}</option>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </select>
      </Campo>

      <Campo etiqueta={t("de.d.dias")} ayuda={t("de.d.diasAyuda")}>
        <div className="fila" style={{ gap: 6 }}>
          {DIAS.map((d) => (
            <button
              key={d}
              type="button"
              className={`boton ${datos.diasSemana.includes(d) ? "" : "secundario"}`}
              style={{ padding: "6px 0", width: 44 }}
              onClick={() => alternarDia(d)}
              title={t(`semana.${d}` as Clave)}
            >
              {t(`semana.${d}c` as Clave)}
            </button>
          ))}
        </div>
      </Campo>

      <div className="linea-campos">
        <Campo etiqueta={t("de.d.desdeLas")} ayuda={t("de.d.franjaAyuda")}>
          <input type="time" value={datos.horaInicio} onChange={(e) => cambiar("horaInicio", e.target.value)} />
        </Campo>
        <Campo etiqueta={t("de.d.hastaLas")}>
          <input type="time" value={datos.horaFin} onChange={(e) => cambiar("horaFin", e.target.value)} />
        </Campo>
      </div>

      <div className="linea-campos">
        <Campo etiqueta={t("de.d.fechaInicio")} ayuda={t("de.d.fechaAyuda")}>
          <input type="date" value={datos.fechaInicio} onChange={(e) => cambiar("fechaInicio", e.target.value)} />
        </Campo>
        <Campo etiqueta={t("de.d.fechaFin")}>
          <input type="date" value={datos.fechaFin} onChange={(e) => cambiar("fechaFin", e.target.value)} />
        </Campo>
      </div>

      <label className="casilla">
        <input type="checkbox" checked={datos.activo} onChange={(e) => cambiar("activo", e.target.checked)} />
        <span>{t("de.d.activo")}</span>
      </label>
    </Modal>
  );
}
